// apps/console/src/hooks/useRobotsRoster.ts
//
// React view over the tenant fleet roster (S1 `GET /robots`). A thin useApiQuery
// wrapper that surfaces the robot list, loading/error state, and a refetch. Read
// by the device_list element (S5) and the fleet_cctv camera lookup (S7, which
// maps a selected robotId → robot.cameraId).
//
// useApiQuery unwraps ApiResponse.data already, so the inner data is
// `{ robots: RobotSummary[] }` — read `data?.robots` ONE level, never
// `data?.data?.robots`.

import type { ApiRequestError } from '../services/api'
import { robotsApi, type RobotSummary } from '../services/api/domains/robots.api'
import { ROSTER_REFETCH_MS } from './roster-timing'
import { useApiQuery } from './useApiQuery'

/** Re-exported so every existing importer is unchanged; the value lives in `roster-timing.ts`. */
export { ROSTER_REFETCH_MS }

export interface UseRobotsRosterResult {
  robots: RobotSummary[]
  isLoading: boolean
  isError: boolean
  error: ApiRequestError | null
  refetch: () => Promise<void>
}

/**
 * @param enabled Fetch and poll only while true. Defaults to true, so every existing caller is
 * unchanged.
 *
 * The escape hatch exists because this hook is now reachable from a component mounted on EVERY
 * console route. Polling the whole fleet every 15s from the map, the library, settings and
 * everywhere else — for a roster only needed in the rare case an alert arrives without a name — is
 * a fleet-wide request on every page for every user, and this deployment has already had to cut
 * concurrent requests once to stay under an AWS Lambda concurrency limit of 10.
 */
export function useRobotsRoster(enabled = true): UseRobotsRosterResult {
  const query = useApiQuery(() => robotsApi.list(), { enabled, refetchInterval: ROSTER_REFETCH_MS })

  return {
    robots: query.data?.robots ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
